import React, { useRef, useCallback } from 'react';
import type { Tile } from '@shared/types';
import { TileComponent } from './TileComponent';

interface TileRackProps {
  tiles: Tile[];
  selectedIds: Set<string>;
  onTileClick: (tileId: string) => void;
  onTilePointerDown: (e: React.PointerEvent<HTMLDivElement>, tileId: string) => void;
  draggingTileId?: string | null;
  onRackPointerUp?: (e: React.PointerEvent<HTMLDivElement>, insertIndex: number) => void;
}

export const TileRack = React.memo(function TileRack({
  tiles,
  selectedIds,
  onTileClick,
  onTilePointerDown,
  draggingTileId,
  onRackPointerUp,
}: TileRackProps) {
  const rowRef = useRef<HTMLDivElement>(null);

  // Calculate insert index from pointer position
  const getInsertIndex = useCallback((clientX: number): number => {
    if (!rowRef.current) return tiles.length;
    const children = Array.from(rowRef.current.querySelectorAll<HTMLElement>('[data-tile-id]'));
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      if (clientX < rect.left + rect.width / 2) return i;
    }
    return tiles.length;
  }, [tiles.length]);

  const handleRackPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const idx = getInsertIndex(e.clientX);
    onRackPointerUp?.(e, idx);
  }, [getInsertIndex, onRackPointerUp]);

  return (
    <div className="rack-wrapper">
      <div
        ref={rowRef}
        className="rack-row"
        data-drop-zone="rack"
        onPointerUp={handleRackPointerUp}
      >
        {tiles.map((tile, idx) => (
          <TileComponent
            key={tile.id}
            tile={tile}
            selected={selectedIds.has(tile.id)}
            isDragging={draggingTileId === tile.id}
            onClick={() => onTileClick(tile.id)}
            onPointerDown={onTilePointerDown}
            animate={false}
          />
        ))}
        {/* Drop zone indicator at end */}
        <div
          className="tile-drop-zone"
          data-drop-zone="rack"
          data-drop-data={String(tiles.length)}
          style={{ opacity: draggingTileId ? 0.6 : 0 }}
        />
      </div>
    </div>
  );
});

export default TileRack;
